import { Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useDrop } from "react-dnd";
import { useSelector } from "react-redux";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext.ts";
import type { DimensionDescriptor } from "../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import { useAppStore } from "../../../../store/store.ts";
import {
  selectFinancialStatementSettings,
  selectSelectedStatementLineId,
  selectStatementAxes,
  selectStatementLines,
} from "../../../../store/selectors/fieldsStateSelectors.ts";
import { selectFieldCaptionLookup, selectMeasures } from "../../../../store/selectors/metaDataSelectors.ts";
import type { StatementLineState } from "../../../../store/types/fieldsState.types.ts";
import { createFinancialStatementAction } from "../../../../store/utils/storeUtils.ts";
import AddField from "../../../viewer/common/fields/area-fields/AddField.tsx";
import { getRemoveBlockedMessage } from "../utils/statementFormulas.ts";
import type { LineDropTarget } from "../utils/statementLines.ts";
import { getLineRails, resolveLinePeriods } from "../utils/statementLines.ts";
import type { RowsDragItem } from "../utils/statementRowsArea.ts";
import {
  breakDownLine,
  createMenuLine,
  findAmountMeasure,
  findMemoAmountMeasure,
  getLineHint,
  getLineHintTooltip,
  getStatementLineLabel,
  getUnresolvedLineWarning,
  resolveRowsDrop,
  rowsAreaItemTypes,
} from "../utils/statementRowsArea.ts";
import AddLineMenu from "./AddLineMenu.tsx";
import FormulaLineDialog from "./FormulaLineDialog.tsx";
import LineCard from "./LineCard.tsx";
import LineOptionsPopover from "./LineOptionsPopover.tsx";

interface LineOptionsTarget {
  guid: string;
  anchorEl: HTMLElement;
}

interface FormulaLineTarget {
  line: StatementLineState;
  isNew: boolean;
}

interface Props {
  readonlyMode: boolean;
}

const RowsArea = ({ readonlyMode }: Props) => {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const { sendNotification } = useNotificationContext();
  const lines = useSelector(selectStatementLines);
  const axes = useSelector(selectStatementAxes);
  const selectedLineId = useSelector(selectSelectedStatementLineId);
  const settings = useSelector(selectFinancialStatementSettings);
  const measures = useSelector(selectMeasures);
  const getFieldCaption = useSelector(selectFieldCaptionLookup);
  const [addLineAnchorEl, setAddLineAnchorEl] = useState<HTMLElement>();
  const [optionsTarget, setOptionsTarget] = useState<LineOptionsTarget>();
  const [formulaTarget, setFormulaTarget] = useState<FormulaLineTarget>();

  const handleDrop = useCallback(
    (item: RowsDragItem, target: LineDropTarget) => {
      const outcome = resolveRowsDrop(item, target, {
        axes,
        amountMeasure: findAmountMeasure(measures),
        memoAmountMeasure: findMemoAmountMeasure(measures),
      });
      if (outcome.kind === "refuse") {
        sendNotification(outcome.message);
      } else if (outcome.kind === "add") {
        dispatch(createFinancialStatementAction("addLines", { lines: outcome.lines, anchor: outcome.anchor }));
      } else if (outcome.kind === "move") {
        dispatch(createFinancialStatementAction("moveLineTo", { guid: outcome.lineGuid, target: outcome.target }));
      }
    },
    [axes, dispatch, measures, sendNotification]
  );

  const handleBreakDown = useCallback(
    (dimension: DimensionDescriptor, guid: string) => {
      const state = store.getState();
      const getCaption = selectFieldCaptionLookup(state);
      const getLabel = (line: StatementLineState): string => getStatementLineLabel(line, getCaption);
      const outcome = breakDownLine(selectStatementLines(state), guid, dimension, {
        axes: selectStatementAxes(state),
        getLabel,
      });
      if (outcome.kind === "apply") {
        const { lines: brokenDownLines, selectedGuid } = outcome;
        dispatch(createFinancialStatementAction("applyLineBreakDown", { lines: brokenDownLines, selectedGuid }));
      }
      sendNotification(outcome.message);
    },
    [dispatch, sendNotification, store]
  );

  const handleSelect = useCallback(
    (guid: string) => dispatch(createFinancialStatementAction("setSelectedLineId", guid)),
    [dispatch]
  );

  const handleRemove = useCallback(
    (guid: string) => dispatch(createFinancialStatementAction("removeLine", guid)),
    [dispatch]
  );

  const handleShowOptions = useCallback(
    (guid: string, anchorEl: HTMLElement) => setOptionsTarget({ guid, anchorEl }),
    []
  );

  const [{ isOver }, drop] = useDrop<RowsDragItem, void, { isOver: boolean }>(
    () => ({
      accept: rowsAreaItemTypes,
      canDrop: () => !readonlyMode,
      drop: (item, monitor) => {
        if (!monitor.didDrop()) {
          handleDrop(item, "end");
        }
      },
      collect: (monitor) => ({ isOver: monitor.isOver({ shallow: true }) && monitor.canDrop() }),
    }),
    [handleDrop, readonlyMode]
  );
  const handleAreaRef = useCallback(
    (element: HTMLDivElement | null) => {
      drop(element);
    },
    [drop]
  );

  const lineCards = useMemo(() => {
    const rails = getLineRails(lines);
    const periods = resolveLinePeriods(lines, settings.defaultDateRange);
    const getLineLabel = (line: StatementLineState): string => getStatementLineLabel(line, getFieldCaption);
    return lines.map((line, index) => ({
      line,
      label: getLineLabel(line),
      hint: getLineHint(line, periods),
      hintTooltip: getLineHintTooltip(line, periods, getFieldCaption),
      warning: getUnresolvedLineWarning(line),
      removeBlockedMessage: getRemoveBlockedMessage(lines, line.guid, getLineLabel),
      rails: rails[index] ?? [],
    }));
  }, [lines, settings.defaultDateRange, getFieldCaption]);
  const optionsLine = optionsTarget && lines.find((line) => line.guid === optionsTarget.guid);

  const getLabel = (line: StatementLineState): string => getStatementLineLabel(line, getFieldCaption);

  const handleAddFormulaLine = () => {
    setAddLineAnchorEl(undefined);
    setFormulaTarget({ line: createMenuLine("Formula", lines.map(getLabel)), isNew: true });
  };

  const handleEditCalculation = (line: StatementLineState) => {
    setOptionsTarget(undefined);
    setFormulaTarget({ line, isNew: false });
  };

  return (
    <Stack sx={{ gap: ".5rem" }}>
      <Typography variant="subtitle2" color="textPrimary">
        Rows
      </Typography>
      <Stack
        ref={handleAreaRef}
        sx={(theme) => ({
          gap: "5px",
          minHeight: readonlyMode ? "40px" : "120px",
          p: ".5rem",
          bgcolor: "background.grey",
          border: `1px solid ${isOver ? theme.palette.primary.main : theme.palette.divider}`,
          borderRadius: "4px",
        })}
      >
        {lineCards.map(({ line, label, hint, hintTooltip, warning, removeBlockedMessage, rails }) => (
          <LineCard
            key={line.guid}
            guid={line.guid}
            lineType={line.lineType}
            label={label}
            hint={hint}
            hintTooltip={hintTooltip}
            warning={warning}
            field={line.field}
            removeBlockedMessage={removeBlockedMessage}
            rails={rails}
            selected={line.guid === selectedLineId}
            readonly={readonlyMode}
            onSelect={handleSelect}
            onDrop={handleDrop}
            onBreakDown={handleBreakDown}
            onShowOptions={handleShowOptions}
            onRemove={handleRemove}
          />
        ))}
        {!readonlyMode && (
          <AddField disabled={false} onAddField={(event) => setAddLineAnchorEl(event.currentTarget)}>
            Add line...
          </AddField>
        )}
      </Stack>
      {addLineAnchorEl && (
        <AddLineMenu
          anchorEl={addLineAnchorEl}
          lineLabels={lines.map(getLabel)}
          onAddFormula={handleAddFormulaLine}
          onClose={() => setAddLineAnchorEl(undefined)}
        />
      )}
      {optionsTarget && optionsLine && (
        <LineOptionsPopover
          line={optionsLine}
          lines={lines}
          defaultRange={settings.defaultDateRange}
          getFieldCaption={getFieldCaption}
          anchorEl={optionsTarget.anchorEl}
          readonly={readonlyMode}
          onEditCalculation={() => handleEditCalculation(optionsLine)}
          onClose={() => setOptionsTarget(undefined)}
        />
      )}
      {formulaTarget && (
        <FormulaLineDialog
          line={formulaTarget.line}
          isNew={formulaTarget.isNew}
          lines={lines}
          getFieldCaption={getFieldCaption}
          onClose={() => setFormulaTarget(undefined)}
        />
      )}
    </Stack>
  );
};

export default RowsArea;
